import { getNavigationId, getNavigationName, getSearchStats, getStoreId, processFindingMethod } from '@libs/analytics';
import { decodeFref, getENV, NavigationListTypes, useCart, useDecisions } from '@libs/common-next';
import type { ItemUI } from '@libs/common-node';

import { useProduct } from '@/entities/product';
import { usePurchaseFormValue } from '@/entities/purchase-form';
import type { FulfilmentMethodSelection } from '@/shared/analytics';
import { dataLayerPush } from '@/shared/analytics';

import {
    buildAddToCartBaseItem,
    buildAddToCartEcommerceData,
    enrichFBTAddToCartEvent,
    type FBTAddToCartBaseEvent,
    type FBTEnrichmentData,
} from '../../lib';

type SendAnalyticsProps = {
    products: ItemUI[];
};

export const useAddToCart = () => {
    const { product, base, tailored } = useProduct();
    const { itemsList } = useCart();

    const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';
    const currency = isCA ? 'CAD' : 'USD';

    const autoshipFlag = useDecisions('ff_autoship');
    const getValues = usePurchaseFormValue();

    const sendAnalytics = ({ products }: SendAnalyticsProps) => {
        const { purchaseOption, fulfilmentMethod } = getValues();

        if (!product || !base || !tailored || !purchaseOption || !fulfilmentMethod) return;

        const { searchTerm } = getSearchStats();
        const frefData = decodeFref();
        const trackingParam = processFindingMethod(window.sessionStorage.pdpFindingMethod);

        const { categories, merchandisingHierarchy } = base;
        const { deliveryMethods } = product;

        const isCustomPharmacyOOS = Boolean(product?.pharmacy?.oos);

        const fulfillmentMethod = 'bopis one-time' satisfies FulfilmentMethodSelection;

        const storeId = getStoreId({
            fulfilmentMethod,
            tailoredVariant: tailored.variants?.[product.sku],
        });

        const tailoredVariant = tailored.variants[product.sku];
        const itemCartOrder = itemsList.length + products.length;

        const totalValue = products.reduce<number>((acc, item) => acc + (item.basePrice ?? 0), 0);
        const roundedValue = Math.round(totalValue * 100) / 100;

        const baseEcommerceData = buildAddToCartEcommerceData({
            categories,
            currency,
            value: roundedValue,
            productRecommendation: 'yes',
        });

        const itemListId = frefData?.item_list_id ?? getNavigationId(trackingParam);
        const itemListName = frefData?.item_list_name ?? getNavigationName(trackingParam);
        const isFromSearch = itemListId === NavigationListTypes.SEARCH;

        const items = products.map(fbtProduct =>
            buildAddToCartBaseItem({
                product: fbtProduct,
                defaultSku: base.defaultSku,
                merchandisingHierarchy,
                fulfillmentMethod,
                storeId,
                searchTerm: isFromSearch ? searchTerm : null,
                itemListId,
                itemListName,
                itemCartOrder,
            })
        );

        const baseEvent: FBTAddToCartBaseEvent = {
            event: 'add_to_cart',
            ecommerce: { ...baseEcommerceData, items },
        };

        const enrichmentData: FBTEnrichmentData =
            autoshipFlag === 'precrawl'
                ? {
                      autoshipFlag: 'precrawl',
                      stockAvailability: 'In Stock',
                  }
                : {
                      autoshipFlag: 'crawl',
                      fbtParams: {
                          mainProductSku: product.sku,
                          deliveryParams: {
                              purchaseOption,
                              deliveryMethods,
                              tailoredVariant,
                              isCustomPharmacyOOS,
                          },
                      },
                  };

        dataLayerPush(enrichFBTAddToCartEvent(baseEvent, enrichmentData));
    };

    return { sendAnalytics };
};
